import { useEffect, useState } from "react";
import api, { fmtErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { TestimonialsEditor } from "./Testimonials";
import { toast } from "sonner";
import { Trash2, Plus, Inbox, Save } from "lucide-react";

const inputCls = "mt-1 w-full bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none";

const InboxTab = () => {
  const [messages, setMessages] = useState(null);
  const load = () => api.get("/admin/contact-messages").then((r) => setMessages(r.data)).catch(() => setMessages([]));
  useEffect(() => { load(); }, []);

  const remove = async (m) => {
    if (!window.confirm(`Delete message from ${m.name}?`)) return;
    try { await api.delete(`/admin/contact-messages/${m.id}`); toast.success("Message deleted"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  if (messages === null) return <p className="text-neutral-500 text-sm">Loading...</p>;
  if (messages.length === 0) return (
    <div className="border border-dashed border-neutral-300 rounded-lg p-12 text-center" data-testid="inbox-empty">
      <Inbox size={24} className="mx-auto text-neutral-300 mb-3" />
      <p className="text-neutral-500 text-sm">No contact form submissions yet.</p>
    </div>
  );
  return (
    <div className="space-y-4" data-testid="contact-inbox-list">
      {messages.map((m) => (
        <div key={m.id} data-testid={`contact-message-${m.id}`} className="bg-white border border-neutral-200 rounded-lg p-6 flex gap-4">
          <div className="flex-1">
            <div className="flex flex-wrap items-baseline gap-x-3">
              <p className="font-semibold text-neutral-900 font-heading">{m.name}</p>
              <a href={`mailto:${m.email}`} className="text-sm text-blue-700 hover:underline">{m.email}</a>
              <span className="text-xs text-neutral-400">{new Date(m.created_at).toLocaleString()}</span>
            </div>
            <p className="text-sm text-neutral-700 mt-2 whitespace-pre-wrap">{m.message}</p>
          </div>
          <button data-testid={`delete-message-${m.id}`} onClick={() => remove(m)}
            className="p-2 h-fit text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md"><Trash2 size={15} /></button>
        </div>
      ))}
    </div>
  );
};

const FaqTab = ({ content, setContent, save, busy }) => {
  const faqs = content?.faqs || [];
  const setFaq = (i, k, v) => setContent((c) => ({ ...c, faqs: c.faqs.map((f, j) => (j === i ? { ...f, [k]: v } : f)) }));
  const addFaq = () => setContent((c) => ({ ...c, faqs: [...c.faqs, { q: "", a: "" }] }));
  const removeFaq = (i) => setContent((c) => ({ ...c, faqs: c.faqs.filter((_, j) => j !== i) }));

  return (
    <div className="space-y-4" data-testid="faq-editor">
      {faqs.map((f, i) => (
        <div key={i} className="bg-white border border-neutral-200 rounded-lg p-5 flex gap-4" data-testid={`faq-item-${i}`}>
          <div className="flex-1">
            <label className="text-xs font-medium text-neutral-500">Question</label>
            <input data-testid={`faq-q-input-${i}`} value={f.q} onChange={(e) => setFaq(i, "q", e.target.value)} className={inputCls} />
            <label className="text-xs font-medium text-neutral-500 mt-3 block">Answer</label>
            <textarea data-testid={`faq-a-input-${i}`} rows={3} value={f.a} onChange={(e) => setFaq(i, "a", e.target.value)} className={inputCls} />
          </div>
          <button data-testid={`faq-remove-${i}`} onClick={() => removeFaq(i)}
            className="p-2 h-fit text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md"><Trash2 size={15} /></button>
        </div>
      ))}
      <div className="flex gap-3">
        <button data-testid="faq-add-button" onClick={addFaq}
          className="px-4 py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-50 inline-flex items-center gap-2">
          <Plus size={15} /> Add question
        </button>
        <button data-testid="faq-save-button" onClick={save} disabled={busy}
          className="px-5 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
          <Save size={15} /> Save FAQ
        </button>
      </div>
    </div>
  );
};

const AboutTab = ({ content, setContent, save, busy }) => {
  const about = content?.about || { heading: "", paragraphs: [] };
  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-6 max-w-3xl" data-testid="about-editor">
      <label className="text-sm font-medium text-neutral-700">Heading</label>
      <input data-testid="about-heading-input" value={about.heading}
        onChange={(e) => setContent((c) => ({ ...c, about: { ...c.about, heading: e.target.value } }))} className={inputCls} />
      <label className="text-sm font-medium text-neutral-700 mt-4 block">Body (separate paragraphs with a blank line)</label>
      <textarea data-testid="about-body-input" rows={12} value={about.paragraphs.join("\n\n")}
        onChange={(e) => setContent((c) => ({ ...c, about: { ...c.about, paragraphs: e.target.value.split(/\n\s*\n/) } }))} className={inputCls} />
      <button data-testid="about-save-button" onClick={save} disabled={busy}
        className="mt-4 px-5 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
        <Save size={15} /> Save About page
      </button>
    </div>
  );
};

export default function Website() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [tab, setTab] = useState(isSuper ? "inbox" : "faq");
  const [content, setContent] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/site-content").then((r) => setContent(r.data)); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const cleaned = {
        about: { heading: content.about.heading, paragraphs: content.about.paragraphs.map((p) => p.trim()).filter(Boolean) },
        faqs: content.faqs.filter((f) => f.q.trim() && f.a.trim()),
      };
      const { data } = await api.put("/admin/site-content", cleaned);
      setContent(data);
      toast.success("Website content saved — live immediately");
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  const tabs = [
    ...(isSuper ? [{ id: "inbox", label: "Contact Inbox" }] : []),
    { id: "faq", label: "FAQ" },
    { id: "about", label: "About Page" },
    { id: "testimonials", label: "Testimonials" },
  ];

  return (
    <div data-testid="website-admin-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-neutral-900 font-heading">Website</h1>
        <p className="text-neutral-500 mt-1">Manage public site content{isSuper ? " and contact form submissions" : ""}.</p>
      </div>
      <div className="flex gap-2 mb-8 flex-wrap">
        {tabs.map((t) => (
          <button key={t.id} data-testid={`website-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium border ${tab === t.id ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-300 text-neutral-600 hover:bg-neutral-50"}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === "inbox" && isSuper && <InboxTab />}
      {tab === "faq" && (content ? <FaqTab content={content} setContent={setContent} save={save} busy={busy} /> : <p className="text-neutral-500 text-sm">Loading...</p>)}
      {tab === "about" && (content ? <AboutTab content={content} setContent={setContent} save={save} busy={busy} /> : <p className="text-neutral-500 text-sm">Loading...</p>)}
      {tab === "testimonials" && <TestimonialsEditor />}
    </div>
  );
}
